import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Checkbox,
  Divider,
  Pagination,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ISelectedEmployeesForInvitation } from 'models/workshopInvitations';
import Search from "components/Search/Search";


interface DualListBoxProps {
  options: ISelectedEmployeesForInvitation[];
  itemsPerPage?: number;
  onSelectedEmployees?: (rightItems: ISelectedEmployeesForInvitation[]) => void; // Callback to access right side data
}

export const DualListBox: React.FC<DualListBoxProps> = ({ options, itemsPerPage = 10, onSelectedEmployees }) => {
  const { t, i18n } = useTranslation();
  const [left, setLeft] = useState<ISelectedEmployeesForInvitation[]>(options);
  const [right, setRight] = useState<ISelectedEmployeesForInvitation[]>([]);
  const [checked, setChecked] = useState<ISelectedEmployeesForInvitation[]>([]);
  const [leftPage, setLeftPage] = useState(1);
  const [rightPage, setRightPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (onSelectedEmployees) {
      onSelectedEmployees(right); // Notify parent whenever the right side changes
    }
  }, [right, onSelectedEmployees]);

  const handleToggle = (item: ISelectedEmployeesForInvitation) => {
    const currentIndex = checked.findIndex((i) => i.id === item.id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(item);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleCheckedRight = () => {
    const newRight = right.concat(checked.filter((item) => left.includes(item)));
    setRight(newRight);
    setLeft(left.filter((item) => !checked.includes(item)));
    setChecked([]);
  };

  const handleCheckedLeft = () => {
    const newLeft = left.concat(checked.filter((item) => right.includes(item)));
    setLeft(newLeft);
    setRight(right.filter((item) => !checked.includes(item)));
    setChecked([]);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
    setChecked([]);
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
    setChecked([]);
  };

  const handleLeftPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setLeftPage(value);
  };

  const handleRightPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setRightPage(value);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setLeftPage(1); // Reset to first page on new search
  };


  // Filter and paginate items on the left side based on the search term
  const filteredLeftItems = left.filter(
    (item) =>
      item.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.departmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.competenciesString.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const paginatedLeftItems = filteredLeftItems.slice(
    (leftPage - 1) * itemsPerPage,
    leftPage * itemsPerPage
  );

  const paginatedRightItems = right.slice((rightPage - 1) * itemsPerPage, rightPage * itemsPerPage);
  const totalLeftPages = Math.ceil(filteredLeftItems.length / itemsPerPage);
  const totalRightPages = Math.ceil(right.length / itemsPerPage);

  const renderTable = (items: ISelectedEmployeesForInvitation[], emptyMessage: string) => {
    if (items.length === 0) {
      return (
        <div style={{ textAlign: 'center', marginBlock: '50px' }}>
          {emptyMessage}
        </div>
      )
    }

    return <TableContainer component={Paper} style={{ height: 400, width: '100%' }}>
      <Table stickyHeader aria-label="items table">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox"></TableCell>
            <TableCell>{t('T_name')}</TableCell>
            <TableCell>{t('T_department')}</TableCell>
            <TableCell>{t('T_competency')}</TableCell>
            <TableCell>{t('T_company')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>

          {items.map((item) => (
            <TableRow key={item.id} hover onClick={() => handleToggle(item)}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={checked.some((i) => i.id === item.id)}
                  onChange={() => handleToggle(item)}
                />
              </TableCell>
              <TableCell>{item.employeeName}</TableCell>
              <TableCell>{item.departmentName}</TableCell>
              <TableCell>{item.competenciesString}</TableCell>
              <TableCell>{i18n.language === 'en' ? item.companyName : item.companyNameAr}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  }

  return (
    <Card>
      <CardContent>

        <Grid container spacing={2} justifyContent="center" alignItems="flex-start">
          {/* Left Side with Search, Table, and Pagination */}

          <Grid item sx={{ minWidth: '80%' }}>
            <div style={{ marginBlock: 15, height: '50px' }}><Search handleChange={handleSearchChange} /></div>

            {renderTable(paginatedLeftItems, "No items found")}
            {paginatedLeftItems.length > 0 && <Pagination
              count={totalLeftPages}
              page={leftPage}
              onChange={handleLeftPageChange}
              color="primary"
              style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}
            />}

          </Grid>

          {/* Buttons for Moving Items */}
          <Grid item alignSelf={'center'}>
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleCheckedRight}
                disabled={checked.filter((item) => left.includes(item)).length === 0 || left.length === 0}
                style={{ marginBottom: 8 }}
              >
                &gt;
              </Button>
              <Button
                variant="outlined"
                onClick={handleAllRight}
                disabled={left.length === 0}
                style={{ marginBottom: 8 }}
              >
                ≫
              </Button>
              <Divider />
              <Button
                variant="outlined"
                onClick={handleCheckedLeft}
                disabled={checked.filter((item) => right.includes(item)).length === 0 || right.length === 0}
                style={{ marginTop: 8 }}
              >
                &lt;
              </Button>
              <Button
                variant="outlined"
                onClick={handleAllLeft}
                disabled={right.length === 0}
                style={{ marginTop: 8 }}
              >
                ≪
              </Button>
            </Box>
          </Grid>

          {/* Right Side with Table, Pagination, and Send Button */}
          <Grid item sx={{ minWidth: '80%' }}>
            <div style={{ marginBlock: 15, height: '50px' }}></div>
            {renderTable(paginatedRightItems, "No items on the right")}
            {paginatedRightItems.length > 0 && <Pagination
              count={totalRightPages}
              page={rightPage}
              onChange={handleRightPageChange}
              color="primary"
              style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}
            />}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
